/**
 * Admin tip console i18n.
 */

import type { TranslationDict } from '../types'

export const adminTips: TranslationDict = {
  // Page shell
  pageTitle: { en: 'Admin · Tips', 'zh-CN': '管理后台 · 打赏', ja: '管理 · 投げ銭' },
  statsTitle: { en: 'Tip stats', 'zh-CN': '打赏统计', ja: '投げ銭統計' },

  // Filters
  filters: {
    payerId: { en: 'Payer ID', 'zh-CN': '打赏者 ID', ja: '送金者 ID' },
    authorId: { en: 'Author ID', 'zh-CN': '作者 ID', ja: '作者 ID' },
    postId: { en: 'Post ID', 'zh-CN': '帖子 ID', ja: '投稿 ID' },
    status: { en: 'Status', 'zh-CN': '状态', ja: 'ステータス' },
    after: { en: 'From', 'zh-CN': '起始时间', ja: '開始日時' },
    before: { en: 'To', 'zh-CN': '截止时间', ja: '終了日時' },
    apply: { en: 'Apply', 'zh-CN': '应用', ja: '適用' },
    reset: { en: 'Reset', 'zh-CN': '重置', ja: 'リセット' },
    all: { en: 'All', 'zh-CN': '全部', ja: 'すべて' },
  },

  // Status badge labels (tip_ledger.status is one of pending / confirmed / reversed;
  // the rest are forward-compat names accepted by the backend whitelist)
  status: {
    confirmed: { en: 'Confirmed', 'zh-CN': '已生效', ja: '確認済' },
    reversed: { en: 'Refunded', 'zh-CN': '已退款', ja: '返金済' },
    refunded: { en: 'Refunded', 'zh-CN': '已退款', ja: '返金済' },
    applied: { en: 'Applied', 'zh-CN': '已生效', ja: '適用済' },
    success: { en: 'Success', 'zh-CN': '成功', ja: '成功' },
    completed: { en: 'Completed', 'zh-CN': '已完成', ja: '完了' },
    pending: { en: 'Pending', 'zh-CN': '处理中', ja: '処理中' },
  },

  // List columns
  list: {
    empty: { en: 'No tips match the filters.', 'zh-CN': '没有匹配的打赏记录。', ja: '該当する投げ銭がありません。' },
    loading: { en: 'Loading…', 'zh-CN': '加载中…', ja: '読み込み中…' },
    error: { en: 'Failed to load: {{message}}', 'zh-CN': '加载失败：{{message}}', ja: '読み込み失敗：{{message}}' },
    columnRef: { en: 'Ref', 'zh-CN': '编号', ja: '参照' },
    columnPayer: { en: 'Payer', 'zh-CN': '打赏者', ja: '送金者' },
    columnAuthor: { en: 'Author', 'zh-CN': '作者', ja: '作者' },
    columnAmount: { en: 'Amount', 'zh-CN': '金额', ja: '金額' },
    columnStatus: { en: 'Status', 'zh-CN': '状态', ja: 'ステータス' },
    columnTime: { en: 'Time', 'zh-CN': '时间', ja: '時間' },
    actions: { en: 'Actions', 'zh-CN': '操作', ja: '操作' },
    refundAction: { en: 'Refund', 'zh-CN': '退款', ja: '返金' },
    detailAction: { en: 'Detail', 'zh-CN': '详情', ja: '詳細' },
  },

  // Stats
  stats: {
    daysWindow: { en: 'Last {{days}} days', 'zh-CN': '近 {{days}} 天', ja: '直近 {{days}} 日' },
    totalCount: { en: 'Tips', 'zh-CN': '打赏笔数', ja: '件数' },
    totalAmount: { en: 'GMV (SQR)', 'zh-CN': 'GMV（SQR）', ja: 'GMV（SQR）' },
    refundedCount: { en: 'Refunded', 'zh-CN': '退款笔数', ja: '返金件数' },
    refundedAmount: { en: 'Refunded amount', 'zh-CN': '退款金额', ja: '返金額' },
    platformShare: { en: 'Platform share', 'zh-CN': '平台分成', ja: 'プラットフォーム取り分' },
    topAuthors: { en: 'Top earning authors', 'zh-CN': '收入最高的作者', ja: '上位作者' },
  },

  // Refund modal
  refund: {
    title: { en: 'Refund tip', 'zh-CN': '退款', ja: '投げ銭を返金' },
    refLabel: { en: 'Reference', 'zh-CN': '编号', ja: '参照' },
    amountLabel: { en: 'Amount to refund', 'zh-CN': '退款金额', ja: '返金額' },
    payerLabel: { en: 'Payer', 'zh-CN': '打赏者', ja: '送金者' },
    authorLabel: { en: 'Author', 'zh-CN': '作者', ja: '作者' },
    amplifierSummary: {
      en: 'Will return SQR to {{payer}}, claw back from {{author}}.',
      'zh-CN': '将退还 SQR 给 {{payer}}，并从 {{author}} 扣回。',
      ja: '{{payer}} に SQR を返還し、{{author}} から取り戻します。',
    },
    confirmLabel: {
      en: 'Type {{phrase}} to enable',
      'zh-CN': '输入 {{phrase}} 以启用',
      ja: '{{phrase}} と入力してください',
    },
    reasonLabel: { en: 'Reason (required)', 'zh-CN': '退款原因（必填）', ja: '理由（必須）' },
    reasonPlaceholder: {
      en: 'e.g. duplicate charge, wrong recipient, payer reported fraud …',
      'zh-CN': '例如：重复扣款、打错对象、举报欺诈……',
      ja: '例：二重請求、誤送金、不正報告など …',
    },
    reasonMin: {
      en: 'At least {{min}} characters',
      'zh-CN': '至少 {{min}} 个字符',
      ja: '最低 {{min}} 文字',
    },
    submit: { en: 'Confirm refund', 'zh-CN': '确认退款', ja: '返金を確定' },
    success: {
      en: 'Refunded {{amount}} SQR to payer.',
      'zh-CN': '已退款 {{amount}} SQR 给打赏者。',
      ja: '送金者に {{amount}} SQR を返金しました。',
    },
    alreadyRefunded: {
      en: 'This tip was already refunded.',
      'zh-CN': '这笔打赏之前已退款。',
      ja: 'この投げ銭は既に返金済みです。',
    },
    pendingDual: {
      en: 'Pending dual approval ({{current}}/{{needed}}).',
      'zh-CN': '需要双签批准（{{current}}/{{needed}}）。',
      ja: '二重承認待ち（{{current}}/{{needed}}）。',
    },
    dualApprovalNote: {
      en: 'High-value refund — a second admin must confirm before money moves.',
      'zh-CN': '大额退款——需要第二位管理员确认后才会真正退钱。',
      ja: '高額返金 — 別の管理者の承認後に資金が移動します。',
    },
    error: {
      notFound: { en: 'Tip not found.', 'zh-CN': '找不到此打赏记录。', ja: '投げ銭が見つかりません。' },
      unrefundable: {
        en: 'This tip cannot be refunded in its current state.',
        'zh-CN': '当前状态不允许退款。',
        ja: '現在の状態では返金できません。',
      },
      generic: { en: 'Refund failed, please retry.', 'zh-CN': '退款失败，请重试。', ja: '返金に失敗しました。再試行してください。' },
    },
  },
}
